'use client';

import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import { classifyHistoryQuery, describeDay, filterHistoryItems, type CallHistoryItem } from '../lib/callHistory';
import { fetchCampaignForm } from '../lib/campaignFormClient';
import { CallExportPanel } from './CallExportPanel';
import { normalizeFormKey, stripAccents, type CampaignFormField, type CampaignFormResponse } from '../lib/campaignFormTypes';

/**
 * Lista "Recentes" do discador mobile.
 *
 * - Data/hora em horário de Brasília (vem pronta da API).
 * - Tipo (Manual/Campanha), nome da campanha, qualificação e motivo da falha.
 * - Reprodução da gravação (via /api/call-audio) para chamadas completadas.
 * - Toque no número/linha abre o detalhe com o formulário da campanha.
 */

type CampaignFormState = {
  status: 'idle' | 'loading' | 'ready' | 'error';
  form: CampaignFormResponse | null;
  error?: string;
};

type RecordingState = {
  status: 'loading' | 'ready' | 'error';
  url?: string;
  error?: string;
};

export type CallHistoryListProps = {
  items: CallHistoryItem[];
  isLoading: boolean;
  error: string | null;
  onRefresh: () => void;
  onCall: (item: CallHistoryItem) => void;
  onWhatsApp: (item: CallHistoryItem) => void;
  /** Base das rotas de API (ex.: https://callix-zeta.vercel.app) */
  apiBaseUrl: string;
  /** Headers com o token de sessão do SDK */
  getAuthHeaders: () => Record<string, string>;
  /** Consulta de busca confirmada pelo pai (controla resets externos) */
  query?: string;
  /** Chamado (com debounce) quando o usuário digita na busca; o pai decide se consulta o servidor */
  onQueryChange?: (query: string) => void;
  /** Busca no servidor em andamento */
  isSearching?: boolean;
  /** Texto de apoio exibido abaixo da busca (ex.: escopo da busca por texto) */
  searchHint?: string | null;
};

const DEFAULT_QUALIFICATION = 'chamada sem qualificação';
const SEARCH_DEBOUNCE_MS = 350;
const DETAILS_TITLE = 'Ver detalhes da chamada';

/**
 * Carrega gravações como blob (o <audio> não consegue mandar o token de sessão).
 * Mantém em memória só as gravações em uso (player aberto ou detalhe aberto);
 * as demais são liberadas via `retain`.
 */
function useRecordings(apiBaseUrl: string, getAuthHeaders: () => Record<string, string>) {
  const [states, setStates] = useState<Record<string, RecordingState>>({});
  const statesRef = useRef(states);
  const urlsRef = useRef<Map<string, string>>(new Map());
  const wantedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    statesRef.current = states;
  }, [states]);

  const revoke = useCallback((id: string) => {
    const url = urlsRef.current.get(id);
    if (url) {
      URL.revokeObjectURL(url);
      urlsRef.current.delete(id);
    }
  }, []);

  useEffect(() => {
    const urls = urlsRef.current;
    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
      urls.clear();
    };
  }, []);

  /** Libera tudo que não está em `keep` (player inline / detalhe aberto). */
  const retain = useCallback(
    (keep: Set<string>) => {
      wantedRef.current = keep;
      const stale = Object.keys(statesRef.current).filter((id) => !keep.has(id));
      if (stale.length === 0) {
        return;
      }
      stale.forEach(revoke);
      setStates((prev) => {
        const next = { ...prev };
        stale.forEach((id) => {
          delete next[id];
        });
        return next;
      });
    },
    [revoke]
  );

  const load = useCallback(
    async (item: CallHistoryItem, force = false) => {
      if (!item.audioPath) {
        return;
      }
      const current = statesRef.current[item.id];
      if (!force && current && (current.status === 'loading' || current.status === 'ready')) {
        return;
      }
      wantedRef.current = new Set([...wantedRef.current, item.id]);
      revoke(item.id);
      setStates((prev) => ({ ...prev, [item.id]: { status: 'loading' } }));
      try {
        if (!item.audioSignature || !item.audioExpiresAt) {
          throw new Error('Gravação indisponível. Atualize o histórico.');
        }
        const query = new URLSearchParams({
          path: item.audioPath,
          exp: String(item.audioExpiresAt),
          sig: item.audioSignature,
        });
        const url = `${apiBaseUrl}/api/call-audio?${query.toString()}`;
        const response = await fetch(url, { method: 'GET', headers: getAuthHeaders(), credentials: 'omit' });
        if (!response.ok) {
          const body = (await response.json().catch(() => ({}))) as { error?: string };
          throw new Error(body.error || `Erro ${response.status}`);
        }
        const blob = await response.blob();
        if (!wantedRef.current.has(item.id)) {
          // o player foi fechado enquanto baixava
          setStates((prev) => {
            const next = { ...prev };
            delete next[item.id];
            return next;
          });
          return;
        }
        revoke(item.id);
        const objectUrl = URL.createObjectURL(blob);
        urlsRef.current.set(item.id, objectUrl);
        setStates((prev) => ({ ...prev, [item.id]: { status: 'ready', url: objectUrl } }));
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Erro ao carregar gravação';
        console.error('[CallHistoryList] Erro ao carregar gravação:', error);
        setStates((prev) => ({ ...prev, [item.id]: { status: 'error', error: message } }));
      }
    },
    [apiBaseUrl, getAuthHeaders, revoke]
  );

  return useMemo(() => ({ states, load, retain }), [states, load, retain]);
}

function OutgoingArrow() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="7" y1="17" x2="17" y2="7" />
      <polyline points="7 7 17 7 17 17" />
    </svg>
  );
}

function IncomingArrow() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="17" y1="7" x2="7" y2="17" />
      <polyline points="17 17 7 17 7 7" />
    </svg>
  );
}

function isDefaultQualification(name: string | undefined): boolean {
  return !name || name.trim().toLowerCase() === DEFAULT_QUALIFICATION;
}

function RecordingPlayer({ state, onRetry, autoPlay }: { state?: RecordingState; onRetry: () => void; autoPlay?: boolean }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const url = state?.status === 'ready' ? state.url : undefined;

  // Tenta tocar assim que o blob chega; se o navegador bloquear (iOS), os controles ficam visíveis.
  useEffect(() => {
    const element = audioRef.current;
    if (!autoPlay || !url || !element || typeof element.play !== 'function') {
      return;
    }
    try {
      const result = element.play();
      if (result && typeof result.catch === 'function') {
        result.catch(() => undefined);
      }
    } catch {
      // ignore
    }
  }, [autoPlay, url]);

  if (!state || state.status === 'loading') {
    return (
      <div className="history-player loading" role="status">
        <i className="fas fa-spinner fa-spin" aria-hidden="true" />
        <span>Carregando gravação...</span>
      </div>
    );
  }
  if (state.status === 'error') {
    return (
      <div className="history-player error" role="alert">
        <i className="fas fa-circle-exclamation" aria-hidden="true" />
        <span>{state.error || 'Erro ao carregar gravação'}</span>
        <button type="button" className="retry-link" onClick={onRetry}>
          Tentar novamente
        </button>
      </div>
    );
  }
  return (
    <div className="history-player">
      <audio ref={audioRef} controls preload="metadata" src={state.url} aria-label="Gravação da chamada" />
    </div>
  );
}

type HistoryRowProps = {
  item: CallHistoryItem;
  dayLabel: string;
  isPlayerOpen: boolean;
  recording?: RecordingState;
  onTogglePlayer: (item: CallHistoryItem) => void;
  onRetryRecording: (item: CallHistoryItem) => void;
  onOpenDetails: (item: CallHistoryItem) => void;
  onCall: (item: CallHistoryItem) => void;
  onWhatsApp: (item: CallHistoryItem) => void;
};

function HistoryRow({
  item,
  dayLabel,
  isPlayerOpen,
  recording,
  onTogglePlayer,
  onRetryRecording,
  onOpenDetails,
  onCall,
  onWhatsApp,
}: HistoryRowProps) {
  const missedClass = item.wasAnswered ? '' : 'missed';
  const stop = (event: MouseEvent) => event.stopPropagation();
  const showQualification = !isDefaultQualification(item.qualificationName);

  let secondary: React.ReactNode = null;
  if (!item.wasAnswered) {
    secondary = (
      <span className="history-item-sub failure">
        <i className="fas fa-circle-exclamation" aria-hidden="true" />
        <span>{item.failureCauseLabel || 'Não completada'}</span>
      </span>
    );
  } else if (item.callType === 'campaign') {
    secondary = (
      <span className={`history-item-sub campaign ${showQualification ? 'with-qualification' : ''}`}>
        <span className="history-campaign-name" title={item.campaignName}>
          {item.campaignName || 'Campanha'}
        </span>
        {showQualification && (
          <>
            <span className="history-meta-dot">•</span>
            <span className="history-qualification" title={item.qualificationName}>
              {item.qualificationName}
            </span>
          </>
        )}
      </span>
    );
  } else if (showQualification) {
    secondary = (
      <span className="history-item-sub">
        <span className="history-qualification" title={item.qualificationName}>
          {item.qualificationName}
        </span>
      </span>
    );
  }

  return (
    <div className={`history-item ${missedClass} ${isPlayerOpen ? 'expanded' : ''}`} data-call-id={item.id}>
      <div className="history-item-row">
        <div className={`history-arrow ${item.wasAnswered ? 'answered' : 'missed'}`}>
          {item.callDirection === 'incoming' ? <IncomingArrow /> : <OutgoingArrow />}
        </div>

        {/* Conteúdo em <span> (phrasing content) para o botão ser HTML válido e o
            leitor de tela ler número, tipo, data, motivo e campanha. */}
        <button type="button" className="history-item-info" onClick={() => onOpenDetails(item)} title={DETAILS_TITLE}>
          <span className={`history-item-number ${missedClass} ${item.callType === 'campaign' ? 'linked' : ''}`}>
            {item.formattedPhone}
          </span>
          <span className="history-item-meta">
            <span className={`history-type-badge ${item.callType}`}>{item.callTypeLabel}</span>
            <span className="history-item-datetime">
              {dayLabel} {item.formattedTime}
            </span>
            {item.wasAnswered && item.duration !== undefined && (
              <span className="history-item-duration">{item.formattedDuration}</span>
            )}
          </span>
          {secondary}
        </button>

        <div className="history-actions">
          {item.hasAudio && (
            <button
              type="button"
              className={`history-action-btn play-btn ${isPlayerOpen ? 'active' : ''}`}
              onClick={(event) => {
                stop(event);
                onTogglePlayer(item);
              }}
              title={isPlayerOpen ? 'Fechar gravação' : 'Ouvir gravação'}
              aria-label={isPlayerOpen ? 'Fechar gravação' : 'Ouvir gravação'}
            >
              <i className={`fas ${isPlayerOpen ? 'fa-xmark' : 'fa-play'}`} aria-hidden="true" />
            </button>
          )}
          <button
            type="button"
            className="history-action-btn call-btn"
            onClick={(event) => {
              stop(event);
              onCall(item);
            }}
            title="Ligar"
            aria-label="Ligar"
          >
            <i className="fas fa-phone" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="history-action-btn whatsapp-btn"
            onClick={(event) => {
              stop(event);
              onWhatsApp(item);
            }}
            title="WhatsApp"
            aria-label="WhatsApp"
          >
            <i className="fab fa-whatsapp" aria-hidden="true" />
          </button>
        </div>
      </div>

      {isPlayerOpen && item.hasAudio && (
        <RecordingPlayer state={recording} onRetry={() => onRetryRecording(item)} autoPlay />
      )}
    </div>
  );
}

function DetailRow({ label, value, tone, wide }: { label: string; value?: string | null; tone?: 'failure' | 'success'; wide?: boolean }) {
  if (!value) {
    return null;
  }
  return (
    <div className={`details-row ${wide ? 'wide' : ''}`}>
      <div className="details-label">{label}</div>
      <div className={`details-value ${tone ?? ''}`}>{value}</div>
    </div>
  );
}

function isInternalFieldKey(key: string): boolean {
  const normalized = key.trim().toLowerCase();
  return /(^|[\s_-])id$/.test(normalized);
}

type CallDetailsSheetProps = {
  item: CallHistoryItem;
  dayLabel: string;
  apiBaseUrl: string;
  getAuthHeaders: () => Record<string, string>;
  recording?: RecordingState;
  onLoadRecording: (item: CallHistoryItem, force?: boolean) => void;
  onClose: () => void;
  onCall: (item: CallHistoryItem) => void;
  onWhatsApp: (item: CallHistoryItem) => void;
};

function CallDetailsSheet({
  item,
  dayLabel,
  apiBaseUrl,
  getAuthHeaders,
  recording,
  onLoadRecording,
  onClose,
  onCall,
  onWhatsApp,
}: CallDetailsSheetProps) {
  const [form, setForm] = useState<CampaignFormState>({ status: 'idle', form: null });
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const isCampaign = item.callType === 'campaign';

  // Foco no botão Fechar ao abrir, Escape fecha, foco volta para quem abriu.
  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }
    const previouslyFocused = document.activeElement as HTMLElement | null;
    closeButtonRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (previouslyFocused && typeof previouslyFocused.focus === 'function' && document.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
    };
  }, [onClose]);

  useEffect(() => {
    if (!isCampaign || !item.campaignContactId) {
      setForm({ status: 'idle', form: null });
      return;
    }

    let cancelled = false;
    setForm({ status: 'loading', form: null });

    // Uma requisição (/api/campaign-form), com cache por contato; o histórico já traz
    // `fields` como fallback (mostrados desde já), por isso sem novas tentativas.
    fetchCampaignForm(
      {
        source: 'campaign',
        id: item.campaignContactId,
        campaignId: item.campaignId ?? null,
        signature: item.formSignature ?? null,
        expiresAt: item.formExpiresAt ?? null,
      },
      { apiBaseUrl, headers: getAuthHeaders(), retries: 0 }
    )
      .then((result) => {
        if (!cancelled) {
          setForm({ status: 'ready', form: result });
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          console.warn('[CallHistoryList] Formulário da campanha indisponível, usando campos do histórico:', error);
          setForm({ status: 'error', form: null, error: error instanceof Error ? error.message : 'Erro ao carregar formulário' });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, getAuthHeaders, isCampaign, item.campaignContactId, item.campaignId, item.id]);

  const fallbackFields = useMemo(
    () =>
      Object.entries(item.fields ?? {}).filter(
        ([key, value]) => value !== undefined && value !== null && String(value).trim() !== '' && !isInternalFieldKey(key)
      ),
    [item.fields]
  );

  // Campos do histórico indexados sem caixa/acentos: completam valores vazios do formulário
  const fallbackByKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const [key, value] of Object.entries(item.fields ?? {})) {
      if (value === undefined || value === null || String(value).trim() === '') continue;
      map.set(stripAccents(normalizeFormKey(key)), String(value));
    }
    return map;
  }, [item.fields]);

  const resolveFieldValue = (field: CampaignFormField): string => {
    if (field.value && field.value.trim() !== '') {
      return field.value;
    }
    const byName = fallbackByKey.get(stripAccents(normalizeFormKey(field.label))) ?? fallbackByKey.get(stripAccents(normalizeFormKey(field.key)));
    return byName ?? '-';
  };

  // Linhas do layout do Callix quando disponíveis; senão, todos os campos numa linha só
  const formRows: CampaignFormField[][] =
    form.status === 'ready' && form.form
      ? form.form.rows && form.form.rows.length > 0
        ? form.form.rows
        : form.form.fields.length > 0
          ? [form.form.fields]
          : []
      : [];
  const hasFormRows = formRows.length > 0;

  let formContent: React.ReactNode;
  if (form.status === 'loading' && fallbackFields.length === 0) {
    formContent = (
      <div className="details-loading" role="status">
        <i className="fas fa-spinner fa-spin" aria-hidden="true" />
        <span>Carregando formulário...</span>
      </div>
    );
  } else if (hasFormRows) {
    formContent = formRows.map((row, rowIndex) => (
      <div key={rowIndex} className="details-form-row">
        {row.map((field, fieldIndex) => (
          <div key={`${rowIndex}-${fieldIndex}-${field.key}`} className="details-field">
            <div className="details-field-label" title={field.label}>
              {field.label}
            </div>
            <div className="details-field-value">{resolveFieldValue(field)}</div>
          </div>
        ))}
      </div>
    ));
  } else if (fallbackFields.length > 0) {
    // Campos gravados no histórico, mostrados desde já; o formulário completo substitui ao chegar
    formContent = (
      <>
        {form.status === 'loading' && (
          <div className="details-loading details-loading-inline" role="status">
            <i className="fas fa-spinner fa-spin" aria-hidden="true" />
            <span>Atualizando formulário...</span>
          </div>
        )}
        <div className="details-form-row">
          {fallbackFields.map(([key, value]) => (
            <div key={key} className="details-field">
              <div className="details-field-label" title={key}>
                {key}
              </div>
              <div className="details-field-value">{value}</div>
            </div>
          ))}
        </div>
      </>
    );
  } else {
    formContent = (
      <div className="details-empty">
        <i className="fas fa-user" aria-hidden="true" />
        <span>{form.status === 'error' ? form.error || 'Formulário indisponível' : 'Nenhum dado do cliente disponível'}</span>
      </div>
    );
  }

  let recordingContent: React.ReactNode = null;
  if (item.hasAudio) {
    if (recording?.status === 'ready') {
      recordingContent = <audio controls preload="metadata" src={recording.url} aria-label="Gravação da chamada" />;
    } else if (recording?.status === 'loading') {
      recordingContent = (
        <div className="details-loading" role="status">
          <i className="fas fa-spinner fa-spin" aria-hidden="true" />
          <span>Carregando gravação...</span>
        </div>
      );
    } else if (recording?.status === 'error') {
      recordingContent = (
        <div className="details-error" role="alert">
          <i className="fas fa-circle-exclamation" aria-hidden="true" />
          <span>{recording.error}</span>
          <button type="button" className="retry-link" onClick={() => onLoadRecording(item, true)}>
            Tentar novamente
          </button>
        </div>
      );
    } else {
      recordingContent = (
        <button type="button" className="details-play-btn" onClick={() => onLoadRecording(item)}>
          <i className="fas fa-play" aria-hidden="true" />
          <span>Ouvir gravação</span>
        </button>
      );
    }
  }

  return (
    <div className="modal-overlay history-details-overlay active" onClick={onClose} role="presentation">
      <div
        className="modal-content history-details-sheet"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Detalhes da chamada para ${item.formattedPhone}`}
      >
        <div className="modal-header">
          <div className="history-details-heading">
            <h2 className="modal-title">{item.formattedPhone}</h2>
            <div className="history-details-subtitle">
              <span className={`history-type-badge ${item.callType}`}>{item.callTypeLabel}</span>
              <span>
                {dayLabel} {item.formattedTimeWithSeconds}
              </span>
            </div>
          </div>
          <button ref={closeButtonRef} type="button" className="modal-close" onClick={onClose} aria-label="Fechar">
            <i className="fas fa-xmark" aria-hidden="true" />
          </button>
        </div>

        <div className="history-details-body">
          {isCampaign && (
            <section className="details-section">
              <div className="details-section-title">Campanha</div>
              <div className="details-campaign-name">{item.campaignName || '-'}</div>
              {item.contactLabel && <div className="details-contact-label">Cliente: {item.contactLabel}</div>}
            </section>
          )}

          <section className="details-section details-grid">
            <DetailRow
              label="Status"
              value={item.wasAnswered ? 'Completada' : 'Não completada'}
              tone={item.wasAnswered ? 'success' : 'failure'}
            />
            <DetailRow label="Duração" value={item.formattedDuration} />
            {!item.wasAnswered && <DetailRow label="Motivo" value={item.failureCauseLabel || 'Não informado'} tone="failure" />}
            {item.wasAnswered && <DetailRow label="Quem desligou" value={item.hangupCauseLabel} />}
            <DetailRow label="Qualificação" value={item.qualificationName} wide />
            <DetailRow label="Protocolo" value={item.protocol ? String(item.protocol) : undefined} />
            <DetailRow label="Data" value={item.formattedDate} />
            <DetailRow label="Observação" value={item.note} wide />
          </section>

          {item.hasAudio && (
            <section className="details-section">
              <div className="details-section-title">Gravação</div>
              {recordingContent}
            </section>
          )}

          {isCampaign && (
            <section className="details-section">
              <div className="details-section-title">Formulário da campanha</div>
              {formContent}
            </section>
          )}
        </div>

        <div className="history-details-actions">
          <button type="button" className="details-action-btn call" onClick={() => onCall(item)}>
            <i className="fas fa-phone" aria-hidden="true" />
            <span>Ligar</span>
          </button>
          <button type="button" className="details-action-btn whatsapp" onClick={() => onWhatsApp(item)}>
            <i className="fab fa-whatsapp" aria-hidden="true" />
            <span>WhatsApp</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export function CallHistoryList({
  items,
  isLoading,
  error,
  onRefresh,
  onCall,
  onWhatsApp,
  apiBaseUrl,
  getAuthHeaders,
  query,
  onQueryChange,
  isSearching = false,
  searchHint = null,
}: CallHistoryListProps) {
  const [openPlayerId, setOpenPlayerId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const recordings = useRecordings(apiBaseUrl, getAuthHeaders);
  const { retain } = recordings;

  // Busca: o texto digitado filtra a lista na hora; o pai recebe a consulta com debounce
  // (para buscar no servidor) e só sobrescreve o campo quando muda a consulta por fora.
  const [draft, setDraft] = useState(query ?? '');
  const lastEmittedRef = useRef(query ?? '');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const external = query ?? '';
    if (external !== lastEmittedRef.current) {
      lastEmittedRef.current = external;
      setDraft(external);
    }
  }, [query]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleDraftChange = useCallback(
    (value: string) => {
      setDraft(value);
      if (!onQueryChange) return;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        lastEmittedRef.current = value;
        onQueryChange(value);
      }, SEARCH_DEBOUNCE_MS);
    },
    [onQueryChange]
  );

  const clearSearch = useCallback(() => {
    setDraft('');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    lastEmittedRef.current = '';
    onQueryChange?.('');
  }, [onQueryChange]);

  const parsedQuery = useMemo(() => classifyHistoryQuery(draft), [draft]);
  const visibleItems = useMemo(() => filterHistoryItems(items, parsedQuery), [items, parsedQuery]);
  const hasQuery = parsedQuery.kind !== 'empty';

  // "Hoje"/"Ontem" calculados a cada atualização da lista
  const dayLabels = useMemo(() => {
    const now = new Date();
    const labels: Record<string, string> = {};
    for (const item of items) {
      labels[item.id] = describeDay(item.dateKey, item.formattedDate, now);
    }
    return labels;
  }, [items]);

  const selectedItem = useMemo(() => items.find((item) => item.id === selectedId) ?? null, [items, selectedId]);

  // Só o player aberto e o detalhe aberto mantêm a gravação em memória.
  useEffect(() => {
    const ids = new Set(items.map((item) => item.id));
    const keep = new Set<string>();
    if (openPlayerId && ids.has(openPlayerId)) keep.add(openPlayerId);
    if (selectedId && ids.has(selectedId)) keep.add(selectedId);
    retain(keep);
  }, [openPlayerId, selectedId, items, retain]);

  const togglePlayer = useCallback(
    (item: CallHistoryItem) => {
      if (openPlayerId === item.id) {
        setOpenPlayerId(null);
        return;
      }
      setOpenPlayerId(item.id);
      void recordings.load(item);
    },
    [openPlayerId, recordings]
  );

  const retryRecording = useCallback(
    (item: CallHistoryItem) => {
      void recordings.load(item, true);
    },
    [recordings]
  );

  const loadRecordingFromDetails = useCallback(
    (item: CallHistoryItem, force = false) => {
      void recordings.load(item, force);
    },
    [recordings]
  );

  // Abrir o detalhe fecha o player inline (evita duas reproduções da mesma gravação).
  const openDetails = useCallback((item: CallHistoryItem) => {
    setOpenPlayerId(null);
    setSelectedId(item.id);
  }, []);

  const closeDetails = useCallback(() => setSelectedId(null), []);

  return (
    <>
      <div className="history-header">
        <h1 className="header-title">Recentes</h1>
        <div className="history-header-actions">
          <button type="button" className={`history-refresh-btn history-export-btn ${exportOpen ? 'active' : ''}`} onClick={() => setExportOpen((open) => !open)} aria-label="Exportar histórico" aria-pressed={exportOpen} title="Exportar áudios ou dados do período">
            <i className="fas fa-file-export" aria-hidden="true" />
          </button>
          <button type="button" className="history-refresh-btn" onClick={onRefresh} disabled={isLoading} aria-label="Atualizar histórico">
            <i className={`fas fa-arrows-rotate ${isLoading ? 'fa-spin' : ''}`} aria-hidden="true" />
          </button>
        </div>
      </div>
      {exportOpen && <CallExportPanel apiBaseUrl={apiBaseUrl} getAuthHeaders={getAuthHeaders} onClose={() => setExportOpen(false)} />}

      <div className="history-search-bar">
        <label className="history-search">
          <i className="fas fa-magnifying-glass history-search-icon" aria-hidden="true" />
          <input
            type="search"
            className="history-search-input"
            placeholder="Buscar número, campanha, cliente..."
            aria-label="Buscar no histórico"
            autoComplete="off"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={draft}
            onChange={(event) => handleDraftChange(event.target.value)}
          />
          {isSearching ? (
            <i className="fas fa-spinner fa-spin history-search-spinner" aria-hidden="true" title="Buscando..." />
          ) : draft ? (
            <button type="button" className="history-search-clear" onClick={clearSearch} aria-label="Limpar busca">
              <i className="fas fa-xmark" aria-hidden="true" />
            </button>
          ) : null}
        </label>
        {hasQuery && searchHint && <div className="history-search-hint">{searchHint}</div>}
      </div>

      <div className="call-history-list" id="callHistoryList">
        {isLoading && items.length === 0 ? (
          <div className="history-loading" role="status">
            <i className="fas fa-spinner fa-spin" aria-hidden="true" />
            <span>Carregando...</span>
          </div>
        ) : error ? (
          <div className="history-error" role="alert">
            <i className="fas fa-exclamation-circle" aria-hidden="true" />
            <span>{error}</span>
            <button type="button" className="retry-btn" onClick={onRefresh}>
              Tentar novamente
            </button>
          </div>
        ) : items.length === 0 ? (
          <div className="history-empty">
            <i className="fas fa-phone-slash" aria-hidden="true" />
            <span>Nenhuma ligação recente</span>
          </div>
        ) : visibleItems.length === 0 ? (
          <div className="history-empty history-empty-search">
            <i className="fas fa-magnifying-glass" aria-hidden="true" />
            <span>
              Nenhuma chamada encontrada para <strong>&ldquo;{draft.trim()}&rdquo;</strong>
            </span>
            {isSearching && <span className="history-search-status">Buscando no servidor...</span>}
          </div>
        ) : (
          visibleItems.map((item) => (
            <HistoryRow
              key={item.id}
              item={item}
              dayLabel={dayLabels[item.id] ?? item.formattedDate}
              isPlayerOpen={openPlayerId === item.id}
              recording={recordings.states[item.id]}
              onTogglePlayer={togglePlayer}
              onRetryRecording={retryRecording}
              onOpenDetails={openDetails}
              onCall={onCall}
              onWhatsApp={onWhatsApp}
            />
          ))
        )}
      </div>

      {selectedItem && (
        <CallDetailsSheet
          item={selectedItem}
          dayLabel={dayLabels[selectedItem.id] ?? selectedItem.formattedDate}
          apiBaseUrl={apiBaseUrl}
          getAuthHeaders={getAuthHeaders}
          recording={recordings.states[selectedItem.id]}
          onLoadRecording={loadRecordingFromDetails}
          onClose={closeDetails}
          onCall={(item) => {
            setSelectedId(null);
            onCall(item);
          }}
          onWhatsApp={onWhatsApp}
        />
      )}
    </>
  );
}
